import { useState } from "react";
import { useTranslation } from "react-i18next";
import inviteBgImg from "@/assets/images/invite_card_fade.png";
import { Icon } from "@/components/icon";
import SplitLine from "@/components/SplitLine";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/sheet";

interface ContributionDegreeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export default function ContributionDegreeDialog({ open, onOpenChange }: ContributionDegreeDialogProps) {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(true);
	const isMobile = useMediaQuery("(max-width: 768px)");

	// 共用的内容
	const content = (
		<>
			{/* Banner */}
			<div className={`mb-4 ${isMobile ? "p-4" : "p-[16px]"} bg-black relative overflow-hidden`}>
				{/* 背景装饰 */}
				<img src={inviteBgImg} alt="" className="absolute top-0 left-0 w-full h-full object-cover" />
				<div className="relative z-10">
					<h2 className={`text-white ${isMobile ? "text-[20px]" : "text-[24px]"} font-normal mb-2`}>
						{t("portfolio.invite.bannerTitle")}
					</h2>
					<p className={`text-white ${isMobile ? "text-[20px]" : "text-[24px]"} font-bold italic`}>
						{t("portfolio.invite.bannerSubtitle")}
					</p>
				</div>
			</div>

			{/* Contribution Degree Section */}
			<div className="border-none shadow-none pb-8">
				<div className="overflow-hidden">
					{/* Collapsible Header */}
					<button
						type="button"
						onClick={() => setIsExpanded(!isExpanded)}
						className="w-full flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-none shadow-none mb-4"
					>
						<h3 className={`${isMobile ? "text-[18px]" : "text-[20px]"} font-bold`}>
							{t("portfolio.invite.contributionTitle")}
						</h3>
						<Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} size={24} className="text-gray-600" />
					</button>

					{/* Collapsible Content */}
					{isExpanded && (
						<div className="pb-6 pt-2 bg-white">
							<ol
								className={`space-y-3 list-decimal list-inside text-gray-700 ${isMobile ? "text-[14px]" : "text-[15px]"} leading-relaxed`}
							>
								<li>{t("portfolio.invite.rule1")}</li>
								<li>{t("portfolio.invite.rule2")}</li>
								<li>{t("portfolio.invite.rule3")}</li>
								<li>{t("portfolio.invite.rule4")}</li>
								<li>{t("portfolio.invite.rule5")}</li>
							</ol>
						</div>
					)}
				</div>

				{/* Done Button */}
				<Button
					onClick={() => onOpenChange(false)}
					className={` w-full ${isMobile ? "h-[48px] text-[16px]" : "h-[56px] text-[18px]"} bg-black hover:bg-gray-800 text-white font-semibold rounded-[8px]`}
				>
					{t("portfolio.invite.doneButton")}
				</Button>
			</div>
		</>
	);

	// 移动端使用 Sheet
	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="bottom" className="h-[85vh] rounded-t-[20px] overflow-y-auto p-0 max-w-full w-[100vw]">
					<div className="px-4 pt-10 pb-2">
						<SplitLine className="mb-1" />
						<SheetHeader className="p-0">
							<SheetTitle className="text-xl font-medium uppercase">{t("portfolio.invite.dialogTitle")}</SheetTitle>
						</SheetHeader>
					</div>
					<div className="px-4 overflow-x-hidden">{content}</div>
				</SheetContent>
			</Sheet>
		);
	}

	// 桌面端使用 Dialog
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[900px] p-0 bg-white rounded-[20px] overflow-hidden px-4">
				{/* Header */}
				<div className="pt-8 pb-2">
					<SplitLine className="mb-1" />
					<DialogHeader>
						<DialogTitle className="text-[24px] font-medium uppercase">{t("portfolio.invite.dialogTitle")}</DialogTitle>
					</DialogHeader>
				</div>
				{content}
			</DialogContent>
		</Dialog>
	);
}
